/**
 * Customer-facing names for the tools an agent runs, in the account language. The runtime stores
 * these on the activity steps, and the web and mobile apps render them as they arrive.
 */
const TOOL_LABELS = {
  generate_image: ["Generate an image", "Gerar uma imagem"],
  generate_video: ["Generate a video", "Gerar um vídeo"],
  computer_observe: ["Look at the screen", "Olhar a tela"],
  computer_act: ["Operate the computer", "Usar o computador"],
  browser_navigate: ["Open a page", "Abrir uma página"],
  browser_snapshot: ["Read the page", "Ler a página"],
  browser_act: ["Use the page", "Usar a página"],
  list_files: ["List files", "Listar arquivos"],
  read_file: ["Read a file", "Ler um arquivo"],
  write_file: ["Write a file", "Escrever um arquivo"],
  attach_file: ["Attach a file", "Anexar um arquivo"],
  create_document: ["Create a document", "Criar um documento"],
  shell: ["Terminal", "Terminal"],
  user_computer_shell: ["Terminal on your computer", "Terminal no seu computador"],
  open_path: ["Open a file", "Abrir um arquivo"],
  launch_app: ["Open an app", "Abrir um aplicativo"],
  request_takeover: ["Ask you to take over", "Pedir para você assumir"],
  connect_app: ["Connect an app", "Conectar um aplicativo"],
  ask_user: ["Ask you", "Perguntar a você"],
  message_user: ["Message you", "Enviar mensagem a você"],
  request_secret: ["Request a secret", "Pedir um segredo"],
  generate_secret: ["Generate a secret", "Gerar um segredo"],
  list_secrets: ["List secrets", "Listar segredos"],
  secret_request: ["Secret request", "Pedido de segredo"],
  forget_secret: ["Forget a secret", "Esquecer um segredo"],
  render_plot: ["Draw a chart", "Criar um gráfico"],
  add_mcp_server: ["Add a connection", "Adicionar uma conexão"],
  remember: ["Remember", "Lembrar"],
  save_shared_memory: ["Save to shared memory", "Salvar na memória compartilhada"],
  save_memory: ["Save to memory", "Salvar na memória"],
  recall_memory: ["Recall memory", "Consultar a memória"],
  forget_memory: ["Forget memory", "Esquecer da memória"],
  web_search: ["Search the web", "Pesquisar na web"],
  web_fetch: ["Read a web page", "Ler uma página da web"],
  search_history: ["Search history", "Pesquisar no histórico"],
  read_history: ["Read history", "Ler o histórico"],
  cloud_agent_launch: ["Start a cloud agent", "Iniciar um agente na nuvem"],
  cloud_agent_status: ["Check a cloud agent", "Verificar um agente na nuvem"],
  cloud_agent_reply: ["Reply to a cloud agent", "Responder a um agente na nuvem"],
  cloud_agent_cancel: ["Cancel a cloud agent", "Cancelar um agente na nuvem"],
  request_purchase: ["Request a purchase", "Solicitar uma compra"],
  purchase_status: ["Check a purchase", "Verificar uma compra"],
  paid_request: ["Paid request", "Solicitação paga"],
  task_catalog: ["Task catalog", "Catálogo de tarefas"],
  scratchpad_list: ["List notes", "Listar anotações"],
  scratchpad_add: ["Add a note", "Adicionar uma anotação"],
  scratchpad_update: ["Update a note", "Atualizar uma anotação"],
  scratchpad_complete: ["Complete a note", "Concluir uma anotação"],
  scratchpad_remove: ["Remove a note", "Remover uma anotação"],
  board_tickets: ["List tickets", "Listar tickets"],
  ticket_get: ["Open a ticket", "Abrir um ticket"],
  ticket_create: ["Create a ticket", "Criar um ticket"],
  ticket_update: ["Update a ticket", "Atualizar um ticket"],
  ticket_comment: ["Comment on a ticket", "Comentar em um ticket"],
  schedule_create: ["Create a routine", "Criar uma rotina"],
  schedule_list: ["List routines", "Listar rotinas"],
  schedule_update: ["Update a routine", "Atualizar uma rotina"],
  schedule_cancel: ["Cancel a routine", "Cancelar uma rotina"],
  call_user: ["Call you", "Ligar para você"],
  end_call: ["End the call", "Encerrar a ligação"],
  skill_read: ["Read a skill", "Ler uma habilidade"],
  skill_create: ["Create a skill", "Criar uma habilidade"],
  skill_update: ["Update a skill", "Atualizar uma habilidade"],
  skill_delete: ["Delete a skill", "Excluir uma habilidade"],
  run_subagent: ["Delegate to a helper", "Delegar a um ajudante"],
  propose_instructions_update: ["Propose new instructions", "Propor novas instruções"],
  create_space: ["Create a space", "Criar um espaço"],
  spawn_bot: ["Create a colleague", "Criar um colega"],
  update_bot: ["Update a colleague", "Atualizar um colega"],
  list_fleet: ["List colleagues", "Listar colegas"],
  create_group: ["Create a group", "Criar um grupo"],
  update_group: ["Update a group", "Atualizar um grupo"],
  organize_bot: ["Organize the sidebar", "Organizar a barra lateral"],
  set_child_instructions: ["Set a colleague's instructions", "Definir as instruções de um colega"],
  set_child_bot_model: ["Set a colleague's model", "Definir o modelo de um colega"],
  create_child_routine: ["Create a colleague's routine", "Criar a rotina de um colega"],
  archive_bot: ["Archive a colleague", "Arquivar um colega"],
  message_bot: ["Message a colleague", "Enviar mensagem a um colega"],
  handoff_to_bot: ["Hand off to a colleague", "Passar para um colega"],
  connect_agent: ["Connect an agent", "Conectar um agente"],
  respond_agent_connection: ["Answer an agent connection", "Responder a uma conexão de agente"],
  message_agent: ["Message an agent", "Enviar mensagem a um agente"],
} as const satisfies Record<string, readonly [english: string, portuguese: string]>;

/** Shown for a tool with no entry, such as a connected app's tool; never the raw identifier. */
const FALLBACK_LABEL = ["Use a tool", "Usar uma ferramenta"] as const;

/** Tool names with a customer-facing label. */
export const LABELLED_TOOL_NAMES: readonly string[] = Object.keys(TOOL_LABELS);

/** Accounts are Portuguese unless their language says English. */
export function isEnglishLocale(locale: string | null | undefined): boolean {
  return (locale ?? "").trim().toLowerCase().split(/[-_]/)[0] === "en";
}

/** The label for a tool name in the account language, or a neutral label for an unknown tool. */
export function toolLabel(name: string, locale?: string | null): string {
  const entry = Object.hasOwn(TOOL_LABELS, name)
    ? TOOL_LABELS[name as keyof typeof TOOL_LABELS]
    : FALLBACK_LABEL;
  return isEnglishLocale(locale) ? entry[0] : entry[1];
}

/** The tool name behind a stored step label in either language, if there is one. */
export function toolNameForLabel(label: string): string | undefined {
  return LABELLED_TOOL_NAMES.find((name) => {
    const entry = TOOL_LABELS[name as keyof typeof TOOL_LABELS];
    return entry[0] === label || entry[1] === label;
  });
}
